import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { TaxAccountApiClient } from '@/clients/taxAccountApiClient';
import { taxAuthorityClosedMessage } from '@/utils/errorUtils';

interface TaxCheckAgainProps {
  accountId: string;
  authorityName: string;
  /** Reloads what the screen shows, so a found balance or the new last-checked time appears. */
  onChecked: () => void | Promise<void>;
  /** `tax-account` on the list row, `tax-detail` on the detail page. */
  testIdPrefix: 'tax-account' | 'tax-detail';
}

/**
 * "Check again" for a company the authority reports no skattekonto for (FIN-1453 F3): one read of
 * the account on demand. The caller decides whether to offer it (`taxRowActions(...).checkAgain`).
 *
 * The button is natively disabled while the read runs, the only guard against a double click.
 */
const TaxCheckAgain: React.FC<TaxCheckAgainProps> = ({ accountId, onChecked, testIdPrefix }) => {
  const t = useTranslations('tax.noAccount');
  const check = useMutation({
    mutationFn: async () => {
      const view = await TaxAccountApiClient.refresh(accountId);
      // Inside the mutation, so "Checking…" holds until the screen shows what the server now holds.
      await onChecked();
      return view;
    },
  });

  const stillNone = check.data && !check.data.balance && check.data.balanceUnavailableReason === 'no_tax_account';
  const failure = check.isError ? (taxAuthorityClosedMessage(check.error) ?? t('checkFailed')) : null;

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        size="sm"
        data-testid={`${testIdPrefix}-check-again`}
        onClick={() => check.mutate()}
        disabled={check.isPending}
      >
        {check.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {check.isPending ? t('checking') : t('checkAgain')}
      </Button>
      {stillNone && (
        <p className="text-xs text-muted-foreground" role="status" data-testid={`${testIdPrefix}-check-result`}>
          {t('stillNone')}
        </p>
      )}
      {failure && (
        <p className="text-xs text-destructive" role="alert" data-testid={`${testIdPrefix}-check-error`}>
          {failure}
        </p>
      )}
    </div>
  );
};

export default TaxCheckAgain;
